import {
  authChangePasswordRequestSchema,
  authChangePasswordResponseSchema,
  authEmailVerifyConfirmRequestSchema,
  authEmailVerifyConfirmResponseSchema,
  authEmailVerifyRequestVerifyResponseSchema,
  authLoginRequestSchema,
  authLoginResponseSchema,
  authLogoutRequestSchema,
  authLogoutResponseSchema,
  authOAuthVerifyRequestSchema,
  authOAuthVerifyResponseSchema,
  authOtpFirebaseVerifyRequestSchema,
  authOtpFirebaseVerifyResponseSchema,
  authOtpSendRequestSchema,
  authOtpSendResponseSchema,
  authOtpVerifyRequestSchema,
  authOtpVerifyResponseSchema,
  authPasswordResetConfirmRequestSchema,
  authPasswordResetConfirmResponseSchema,
  authPasswordResetRequestSchema,
  authPasswordResetResponseSchema,
  authRefreshRequestSchema,
  authRefreshResponseSchema,
  authRegisterRequestSchema,
  authRegisterResponseSchema,
  authRegisterStartRequestSchema,
  authRegisterStartResponseSchema,
  authRegisterCompleteRequestSchema,
  authRegisterCompleteResponseSchema,
  type AuthChangePasswordRequest,
  type AuthEmailVerifyConfirmRequest,
  type AuthLoginRequest,
  type AuthLogoutRequest,
  type AuthOAuthVerifyRequest,
  type AuthOtpFirebaseVerifyRequest,
  type AuthOtpSendRequest,
  type AuthOtpVerifyRequest,
  type AuthPasswordResetConfirmRequest,
  type AuthPasswordResetRequest,
  type AuthRefreshRequest,
  type AuthRegisterRequest,
  type AuthRegisterStartRequest,
  type AuthRegisterCompleteRequest,
} from "@chotca/config";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export function createAuthResource(ctx: RequestContext) {
  return {
    // Email + password (web)
    register(body: AuthRegisterRequest) {
      const payload = authRegisterRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/register",
        body: payload,
        dataSchema: authRegisterResponseSchema,
        auth: false,
      });
    },

    registerStart(body: AuthRegisterStartRequest) {
      const payload = authRegisterStartRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/register/start",
        body: payload,
        dataSchema: authRegisterStartResponseSchema,
        auth: false,
      });
    },

    registerComplete(body: AuthRegisterCompleteRequest) {
      const payload = authRegisterCompleteRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/register/complete",
        body: payload,
        dataSchema: authRegisterCompleteResponseSchema,
        auth: false,
      });
    },

    loginWithPassword(body: AuthLoginRequest) {
      const payload = authLoginRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/login",
        body: payload,
        dataSchema: authLoginResponseSchema,
        auth: false,
      });
    },

    requestEmailVerify() {
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/email-verify/request",
        dataSchema: authEmailVerifyRequestVerifyResponseSchema,
        auth: true,
      });
    },

    confirmEmailVerify(body: AuthEmailVerifyConfirmRequest) {
      const payload = authEmailVerifyConfirmRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/email-verify/confirm",
        body: payload,
        dataSchema: authEmailVerifyConfirmResponseSchema,
        auth: false,
      });
    },

    requestPasswordReset(body: AuthPasswordResetRequest) {
      const payload = authPasswordResetRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/password-reset/request",
        body: payload,
        dataSchema: authPasswordResetResponseSchema,
        auth: false,
      });
    },

    confirmPasswordReset(body: AuthPasswordResetConfirmRequest) {
      const payload = authPasswordResetConfirmRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/password-reset/confirm",
        body: payload,
        dataSchema: authPasswordResetConfirmResponseSchema,
        auth: false,
      });
    },

    changePassword(body: AuthChangePasswordRequest) {
      const payload = authChangePasswordRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/password/change",
        body: payload,
        dataSchema: authChangePasswordResponseSchema,
        auth: true,
      });
    },

    // OTP (worker / mobile)
    sendOtp(body: AuthOtpSendRequest) {
      const payload = authOtpSendRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/otp/send",
        body: payload,
        dataSchema: authOtpSendResponseSchema,
        auth: false,
      });
    },

    verifyOtp(body: AuthOtpVerifyRequest) {
      const payload = authOtpVerifyRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/otp/verify",
        body: payload,
        dataSchema: authOtpVerifyResponseSchema,
        auth: false,
      });
    },

    // Social login — Google + Apple (worker / mobile)
    oauthVerify(body: AuthOAuthVerifyRequest) {
      const payload = authOAuthVerifyRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/oauth/verify",
        body: payload,
        dataSchema: authOAuthVerifyResponseSchema,
        auth: false,
      });
    },

    firebaseVerifyOtp(body: AuthOtpFirebaseVerifyRequest) {
      const payload = authOtpFirebaseVerifyRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/otp/firebase-verify",
        body: payload,
        dataSchema: authOtpFirebaseVerifyResponseSchema,
        auth: false,
      });
    },

    // Session lifecycle
    refresh(body: AuthRefreshRequest) {
      const payload = authRefreshRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/refresh",
        body: payload,
        dataSchema: authRefreshResponseSchema,
        auth: false,
      });
    },

    logout(body: AuthLogoutRequest = {}) {
      const payload = authLogoutRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/auth/logout",
        body: payload,
        dataSchema: authLogoutResponseSchema,
        auth: true,
      });
    },
  };
}
